import { useContext, useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { WppTypography } from '@wppopen/components-library-react'
import PdpView from './PdpView/PdpView'
import PdpHeader from './PdpHeader/PdpHeader'
import BackNavigationButton from 'components/BackNavigationButton/BackNavigationButton'
import PdpContentActions from './PdpView/PdpContentActions'
import ParentPdp from './ParentPdp/ParentPdp'
import { headerMetaData } from './PdpHeader/utils'
import { ExpandablePopover } from 'components/KeywordsElements'
import { GenericModal } from 'components/Modals'
import AiOutput from 'components/AiOutput/AiOutput'
import appContext from 'context/appContext'
import Banner from 'components/Banner'
import useApi from 'hooks/useApi'
import { downloadCSVFile, getFormattedDate, getUrlFromEndpoint } from 'utils'
import ViewTaskPageContext from 'context/viewTaskPageContext/viewTaskPageContext'
import NotFound404Page from 'pages/NotFound404Page/NotFound404Page'
import {
  mapReducerStateToKeywordsData,
  setKeyphrases,
  setKeywords,
} from 'reducers/KeywordsAndPhrasesReducer/KeywordsAndPhrasesReducer'
import { DEFAULT_TAB, SUCCESS_SUBMIT_BANNER } from './utils'
import useToast from 'hooks/useToast'
import { renderBackNavigationText } from 'components/BackNavigationButton/utils'
import { ProductDataForm } from 'components/Forms'
import VariantDataForm from 'components/Forms/VariantDataForm/VariantDataForm'
import usePermissions from 'hooks/usePermissions'
import useCurrentUser from 'hooks/useCurrentUser'
import useInitialiser from 'hooks/useInitialiser'
import clsx from 'clsx'
import { v5, type ResubmitCreateData } from '@contracts'
import type { pdpTabIds } from 'components/Tabs/tabs-type.d'
import type { FEApiError } from 'utils/errorHandling'
import navigationHeaderStyles from '../Settings/NavigationHeader.module.scss'
import styles from './ViewTaskPage.module.scss'
import AiOutputSkeleton from 'components/Skeletons/AiOutputSkeleton'

function ViewTaskPage() {
  const { history, configApi } = useContext(appContext)
  const { job_id } = useParams()
  const shouldShowBackNavigationButton = !!history.length && !!history?.[1] //&& history?.[2] !== `/task/${job_id}`
  const {
    contentTypeBeingEdited,
    isPageEditable,
    setIsPageEditable,
    setSingleJobApiMethods,
    keywordsAndPhrasesReducerstate,
    keywordsAndPhrasesReducerDispatch,
    shouldShowKeywords,
    setShouldShowKeywords,
    isResubmitModalOpen,
    setIsResubmitModalOpen,
  } = useContext(ViewTaskPageContext)
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false)
  const { toastDetails, setToastDetails } = useToast(true)
  const [activeTab, setActiveTab] = useState<pdpTabIds>('content')
  const getSingleJobApiMethods = useApi<v5.JobsDetailData>(`jobs/${job_id}`)

  const { userName } = useCurrentUser()

  // useApi singleJobData
  const {
    fetchData: getSingleJob,
    data: getSingleJobData,
    error: getSingleJobError,
    isLoading: isGetSingleJobLoading,
  } = getSingleJobApiMethods

  const getRetailerUrl = useMemo(() => {
    return {
      retailer_url: `retailers/${getSingleJobData?.config?.retailer?.id}`,
      retailer_version_url: `retailers/${getSingleJobData?.config?.retailer?.id}/version/${getSingleJobData?.config?.retailer?.version}`,
    }
  }, [getSingleJobData?.config?.retailer?.id, getSingleJobData?.config?.retailer?.version])

  // useApi retailer
  const getRetailerByVersionApiMethods = useApi<v5.VersionDetailOutput>('')
  const getRetailerApiMethods = useApi<v5.RetailersDetailData>('')

  const getCategoryByVersionApiMethods = useApi<v5.VersionDetailOutput>('')
  const getCategoryApiMethods = useApi<v5.CategoriesDetailData>('')

  const {
    fetchData: getCategoryByVersion,
    data: getCategoryByVersionData,
    isLoading: isGetCategoryByVersionLoading,
    setUrl: getCategoryByVersionSetUrl,
    url: getCategoryByVersionUrl,
  } = getCategoryByVersionApiMethods

  const {
    fetchData: getCategory,
    data: getCategoryData,
    isLoading: isGetCategoryLoading,
    setUrl: getCategorySetUrl,
  } = getCategoryApiMethods

  const {
    fetchData: getRetailerByVersion,
    data: getRetailerByVersionData,
    isLoading: isGetRetailerByVersionLoading,
    setUrl: getRetailerByVersionSetUrl,
    url: getRetailerByVersionUrl,
  } = getRetailerByVersionApiMethods

  const {
    fetchData: getRetailer,
    data: getRetailerData,
    isLoading: isGetRetailerLoading,
    setUrl: getRetailerSetUrl,
  } = getRetailerApiMethods

  const getCategoryUrl = useMemo(() => {
    const categoryId = getSingleJobData?.config?.category?.id
    const categoryVersion = getSingleJobData?.config?.category?.version
    if (!categoryId) {
      return {
        category_url: '',
        category_version_url: '',
      }
    }
    return {
      category_url: `categories/${categoryId}`,
      category_version_url: `categories/${categoryId}/version/${categoryVersion}`,
    }
  }, [getSingleJobData?.config?.category?.id, getSingleJobData?.config?.category?.version])

  // useApi get category

  // post resubmit job
  const {
    fetchData: postResubmitJob,
    url: resubmitJobUrl,
    isLoading: isResubmitJobLoading,
  } = useApi<ResubmitCreateData | Response>(`jobs/${job_id}/resubmit`, {}, 0, 'json', true)

  // useApi exportedSingleJobData
  const {
    fetchData: getExportSingleJob,
    data: getExportSingleJobData,
    isLoading: isGetExportSingleJobLoading,
    error: getExportSingleJobError,
  } = useApi<string>(`jobs/${job_id}/export`, {}, 0, 'csv')

  // useApi approvedJobs
  const { fetchData: postApproveJob, isLoading: isPostApproveJobLoading } = useApi<v5.JobsDetailData>(
    `/jobs/${getSingleJobData?._id}/approve`,
  )

  const { clientLevelPermissions } = usePermissions({
    clientId: getSingleJobData?.client_id,
    permissionSubset: ['PDP_APP_GET_JOBS_BY'],
  })

  const [canGetJob] = clientLevelPermissions
  const jobProductData = getSingleJobData?.config?.product
  const jobVariantData = getSingleJobData?.config?.variant
  const { isInitialising } = useInitialiser({ subscribedApis: [getSingleJobApiMethods] })

  // for some reason the scroll persist from prev page to PDP scroll to top of the page when the page loads and if the job_id changes reset the active tab to default
  useEffect(() => {
    window.scrollTo(0, 0)
    if (activeTab !== DEFAULT_TAB) setActiveTab(DEFAULT_TAB)
  }, [job_id])

  useEffect(() => {
    setSingleJobApiMethods(getSingleJobApiMethods)
  }, [getSingleJobApiMethods.data])

  useEffect(() => {
    getSingleJob()
      .then(res => {
        keywordsAndPhrasesReducerDispatch(setKeywords(res))
        keywordsAndPhrasesReducerDispatch(setKeyphrases(res))
      })
      .catch(console.error)
  }, [job_id])

  // update the retailer URLs
  useEffect(() => {
    const retailerId = getSingleJobData?.config?.retailer?.id
    const categoryId = getSingleJobData?.config?.category?.id

    if (retailerId) {
      // Get retailer by version if URL is available
      if (getRetailerUrl.retailer_version_url && getRetailerUrl.retailer_version_url !== '') {
        getRetailerByVersionSetUrl(getUrlFromEndpoint(getRetailerUrl.retailer_version_url))
      }

      // Get retailer if URL is available
      if (getRetailerUrl.retailer_url && getRetailerUrl.retailer_url !== '') {
        getRetailerSetUrl(getUrlFromEndpoint(getRetailerUrl.retailer_url))
      }
    }

    if (categoryId) {
      // Get category by version if URL is available
      if (getCategoryUrl.category_version_url && getCategoryUrl.category_version_url !== '') {
        getCategoryByVersionSetUrl(getUrlFromEndpoint(getCategoryUrl.category_version_url))
      }

      // Get category if URL is available
      if (getCategoryUrl.category_url && getCategoryUrl.category_url !== '') {
        getCategorySetUrl(getUrlFromEndpoint(getCategoryUrl.category_url))
      }
    }
  }, [getSingleJobData?.config?.retailer, getSingleJobData?.config?.category])

  useEffect(() => {
    // For retailer data
    if (activeTab === 'retailer_rules') {
      if (
        !getRetailerByVersionData &&
        getRetailerUrl.retailer_version_url &&
        getRetailerUrl.retailer_version_url !== ''
      ) {
        getRetailerByVersion(getRetailerByVersionUrl, {
          method: 'GET',
        }).catch(console.error)
      }
      if (!getRetailerData && getRetailerUrl.retailer_url && getRetailerUrl.retailer_version_url !== '') {
        getRetailer(getRetailerUrl.retailer_url, {
          method: 'GET',
        }).catch(console.error)
      }
    }

    // For category data
    if (activeTab === 'category_rules') {
      if (
        !getCategoryByVersionData &&
        getCategoryUrl.category_version_url &&
        getCategoryUrl.category_version_url !== ''
      ) {
        getCategoryByVersion(getCategoryByVersionUrl, {
          method: 'GET',
        }).catch(console.error)
      }
      if (!getCategoryData && getCategoryUrl.category_url && getCategoryUrl.category_version_url !== '') {
        getCategory(getCategoryUrl.category_url, {
          method: 'GET',
        }).catch(console.error)
      }
    }
  }, [
    activeTab,
    getRetailerUrl.retailer_version_url,
    getRetailerUrl.retailer_url,
    getCategoryUrl.category_version_url,
    getCategoryUrl.category_url,
  ])

  const popoverTabKeywordsData = useMemo(() => {
    const mappedKeywordsData = mapReducerStateToKeywordsData(keywordsAndPhrasesReducerstate)

    return mappedKeywordsData
  }, [keywordsAndPhrasesReducerstate, getSingleJobData?.type])

  useEffect(() => {
    if (!isGetExportSingleJobLoading && !getExportSingleJobError && getExportSingleJobData) {
      downloadCSVFile(getExportSingleJobData, `task${job_id}.csv`)
      getSingleJob().catch(console.error)
    }
  }, [isGetExportSingleJobLoading])

  const onDownloadClick = async () => {
    getExportSingleJob().catch(() => {
      setToastDetails({ message: 'Error: Unable to download tasks', type: 'error' })
    })
  }

  const onResubmitClick = async () => {
    try {
      const resubmitRes = await postResubmitJob(resubmitJobUrl, {
        method: 'POST',
      })

      if (Reflect.has(resubmitRes, 'ok') && !(resubmitRes as Response).ok) {
        throw new Error('Something went wrong')
      }
      await getSingleJob()
      setToastDetails(SUCCESS_SUBMIT_BANNER)
      setIsResubmitModalOpen(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const onApproveClick = async () => {
    try {
      const approveRes = await postApproveJob(`/jobs/${getSingleJobData?._id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ user_name: userName }),
      })

      if (Reflect.has(approveRes, 'ok')) {
        throw new Error('Something went wrong')
      }
      await getSingleJob()
      setToastDetails({
        message: 'Task approved successfully',
        type: 'success',
      })
      setIsApprovalModalOpen(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const { data: configData } = configApi
  const regionData = configData?.regions?.find(region => region._id === getSingleJobData?.region_id)

  // PDP header data
  const headerData = useMemo(() => headerMetaData(getSingleJobData, regionData), [getSingleJobData, regionData])

  const expandablePopoverElement = useMemo(() => {
    return getSingleJobData && <ExpandablePopover isPopoverActive={shouldShowKeywords} data={popoverTabKeywordsData} />
  }, [getSingleJobData, shouldShowKeywords, popoverTabKeywordsData])

  const isConfigTab = ['brand_rules', 'retailer_rules', 'product_info'].includes(activeTab)

  if (!isInitialising && (getSingleJobError?.status === 404 || !canGetJob)) return <NotFound404Page />

  return (
    <>
      <div className="dark-bg-page ">
        <header className={clsx('light-header', styles.headerContainer)}>
          <div className={navigationHeaderStyles.navigationHeader__container}>
            {shouldShowBackNavigationButton && (
              <BackNavigationButton
                href={history?.[1]}
                text={renderBackNavigationText(history?.[1])}
                isBottomMargin={false}
              />
            )}
            <Banner
              shouldBannerShow={!!toastDetails}
              text={toastDetails?.message}
              type={toastDetails?.type}
              onClose={() => setToastDetails(undefined)}
            />
            {getSingleJobError && (
              <WppTypography className={styles.unavailable} type="s-midi">
                Error fetching job data. Please try again later
              </WppTypography>
            )}
          </div>
          <div className={styles.headerMetadata}>
            <PdpHeader
              jobData={getSingleJobData}
              headerData={headerData}
              actions={{
                onDownloadClick,
                onApproveClick: () => setIsApprovalModalOpen(true),
                isDownloading: isGetExportSingleJobLoading,
              }}
              tabsAndKeywords={{
                activeTab,
                onActiveTabChange: setActiveTab,
              }}
              isLoading={isGetSingleJobLoading}
            />
          </div>
        </header>

        <section className={clsx('page-content', styles.contentContainer)}>
          <section data-testid="content-section" className={clsx(isConfigTab && styles.contentInfoContainer)}>
            {/* content */}
            {activeTab === 'content' ? (
              <section data-testid="pdp-content">
                <PdpContentActions
                  isInitialising={isInitialising}
                  jobStatus={getSingleJobData?.status}
                  toggleActions={{
                    isActive: shouldShowKeywords,
                    onChange: () => setShouldShowKeywords(!shouldShowKeywords),
                  }}
                  editActions={{
                    isActive: !!contentTypeBeingEdited,
                    onClick: () => setIsPageEditable(!isPageEditable),
                  }}
                />
                <PdpView
                  singleJobData={getSingleJobData}
                  isSingleJobLoading={isGetSingleJobLoading}
                  actions={{
                    onDownloadClick,
                    onApproveClick: () => setIsApprovalModalOpen(true),
                    isDownloading: isGetExportSingleJobLoading,
                  }}
                  isInitialising={isInitialising}
                  setToastDetails={setToastDetails}
                />

                {expandablePopoverElement}
              </section>
            ) : activeTab === 'parent_content' ? (
              <ParentPdp
                parentJobId={
                  getSingleJobData?.type === 'sku_adaption'
                    ? getSingleJobData.config?.retailer_adaption_job_id
                    : getSingleJobData?.config?.standard_content_job_id
                }
                parentJobRegionData={regionData}
              />
            ) : activeTab === 'product_data' && !!jobProductData ? (
              <section data-testid="product-data-form">
                <ProductDataForm productData={jobProductData} isFormActive={false} />
              </section>
            ) : activeTab === 'variant_data' && !!jobVariantData ? (
              <section data-testid="variant-data-form">
                <VariantDataForm variantData={jobVariantData} isFormActive={false} isPatchVariantLoading={false} />
              </section>
            ) : (activeTab === 'retailer_rules' &&
                isGetRetailerByVersionLoading &&
                isGetRetailerLoading &&
                !getRetailerData &&
                !getRetailerByVersionData) ||
              (activeTab === 'category_rules' &&
                isGetCategoryByVersionLoading &&
                isGetCategoryLoading &&
                !getCategoryData &&
                !getCategoryByVersionData) ? (
              <AiOutputSkeleton />
            ) : (
              <AiOutput
                activeTab={activeTab}
                jobData={getSingleJobData}
                latestRetailerData={getRetailerData}
                retailerData={{
                  ...getRetailerByVersionData!,
                  latest_version: (getRetailerData as any)?.version || 0,
                  version_used: getSingleJobData?.config?.retailer?.version || 0,
                }}
                categoryData={{
                  ...getCategoryByVersionData!,
                  latest_version: (getCategoryData as any)?.version || 0,
                  version_used: getSingleJobData?.config?.category?.version || 0,
                }}
                latestCategoryData={getCategoryData}
              />
            )}
          </section>
        </section>
      </div>

      {/* approve modal */}
      {isApprovalModalOpen && (
        <GenericModal
          shouldModalOpen={isApprovalModalOpen}
          modalTitle="Approve task"
          submitButtonText="Approve"
          isApiLoading={isPostApproveJobLoading || isGetSingleJobLoading}
          onCancel={() => setIsApprovalModalOpen(false)}
          onSubmit={onApproveClick}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-message">
            Date & time: {getFormattedDate(new Date())}
          </WppTypography>
        </GenericModal>
      )}

      {/* resubmit modal */}
      {isResubmitModalOpen && (
        <GenericModal
          shouldModalOpen={isResubmitModalOpen}
          modalTitle="Resubmit task"
          submitButtonText="Resubmit"
          onCancel={() => setIsResubmitModalOpen(false)}
          onSubmit={onResubmitClick}
          isApiLoading={isResubmitJobLoading}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-message">
            Are you sure you want to resubmit this task?
            <br />
            This will generate the content using the latest rules.
          </WppTypography>
        </GenericModal>
      )}
    </>
  )
}

export default ViewTaskPage
